import { Injectable, inject, signal } from '@angular/core';
import {
  AlertaApi, DatosDeAlerta, ItemDeInspiracion, NuevaAlertaDto, ParametrosDeEstimacion, RespuestaAlerta, RespuestaAlertas, RespuestaRecibidas,
} from 'src/app/interfaces/alert';
import { SessionService } from '../session/session.service';

/** Respuesta genérica de la API: `ok` y, si no, `reason`. */
interface RespuestaOk {
  ok: boolean;
  reason?: string;
}

/**
 * La única puerta de la web a `/alerts` (gestión avanzada de alertas, S14).
 *
 * Va directo a los métodos genéricos de michollo-client (`session.mc.get/put/patch/post/
 * delete`), que ya ponen el JWT y reintentan tras refrescarlo; no pasa por `RestService`.
 * Los ids de alerta son cadenas OPACAS (`t-12`, `k-34`): aquí se concatenan a la ruta y
 * nada más. Es seguro en SSR: no toca `window` ni `localStorage`; el cliente lo construye
 * `SessionService` con sus propias guardas.
 */
@Injectable({ providedIn: 'root' })
export class AlertasService {
  private session = inject(SessionService);

  /**
   * Alertas recibidas sin leer (`unread` de `GET /alerts/received`). Lo comparten la pestaña
   * Alertas de la barra inferior, que lo suma a los avisos de la campana, y la sub-pestaña
   * «Recibidas», que lo baja al marcar (odd/tasks/avisos-movil.md, S3, S5).
   */
  readonly recibidasSinLeer = signal(0);

  /** Pide una sola fila de recibidas y se queda con `unread`; si falla, deja el que había. */
  async contarRecibidasSinLeer(): Promise<void> {
    const resp = await this.recibidas(1, 0);
    if (resp?.ok && typeof resp.unread === 'number') this.recibidasSinLeer.set(Math.max(0, resp.unread));
  }

  /** `GET /alerts`: la lista entera, la comunidad y los canales del usuario. */
  listar(): Promise<RespuestaAlertas> {
    return this.pedir(() => this.session.mc.get('alerts', undefined));
  }

  /** `PUT /alerts`: crea (o reactiva) una alerta de etiqueta o de palabra. */
  crear(dto: NuevaAlertaDto): Promise<RespuestaAlerta> {
    return this.pedir(() => this.session.mc.put('alerts', dto));
  }

  /** `PATCH /alerts/:id`: cambia sólo los campos que vienen. `kmh: -1` apaga la campana de una etiqueta. */
  editar(id: string, dto: Partial<DatosDeAlerta>): Promise<RespuestaAlerta> {
    return this.pedir(() => this.session.mc.patch(`alerts/${id}`, dto));
  }

  /** `DELETE /alerts/:id`: una palabra se borra; una etiqueta queda seguida con la campana apagada. */
  quitar(id: string): Promise<RespuestaOk> {
    return this.pedir(() => this.session.mc.delete(`alerts/${id}`));
  }

  /**
   * `DELETE /tags/:id/subscription`: deja de seguir la etiqueta del todo. Es lo que hace
   * desaparecer su alerta de la lista; `quitar` sólo le apaga la campana y la API la
   * seguiría devolviendo como «Sin avisos».
   */
  dejarDeSeguirEtiqueta(tagId: number): Promise<RespuestaOk> {
    return this.pedir(() => this.session.mc.delete(`tags/${tagId}/subscription`));
  }

  /** `PUT /alerts/community`: enciende o apaga «Chollos supersónicos». */
  comunidad(supersonic: boolean): Promise<RespuestaOk & { community?: { supersonic: boolean } }> {
    return this.pedir(() => this.session.mc.put('alerts/community', { supersonic }));
  }

  /** `GET /alerts/received`: los chollos que dispararon alguna alerta, paginados. */
  recibidas(limit = 20, offset = 0): Promise<RespuestaRecibidas> {
    return this.pedir(() => this.session.mc.get('alerts/received', { limit, offset }));
  }

  /** `POST /alerts/received/read`: marca leídas unas filas o todas. */
  marcarLeidas(ids: number[] | 'all'): Promise<RespuestaOk & { unread?: number }> {
    const cuerpo = ids === 'all' ? { all: true } : { ids };
    return this.pedir(() => this.session.mc.post('alerts/received/read', cuerpo));
  }

  /** `GET /alerts/inspiration`: etiquetas y palabras con más alertas creadas, sin las del usuario. */
  inspiracion(): Promise<RespuestaOk & { items?: ItemDeInspiracion[] }> {
    return this.pedir(() => this.session.mc.get('alerts/inspiration', undefined));
  }

  /**
   * `GET /alerts/estimate`: cuántos chollos por semana habrían coincidido. Los precios sin
   * filtro viajan como cadena vacía, que es como un formulario los pondría en la query.
   */
  estimar(params: ParametrosDeEstimacion): Promise<RespuestaOk & { per_week?: number }> {
    const query = { ...params, price_min: params.price_min ?? '', price_max: params.price_max ?? '' };
    return this.pedir(() => this.session.mc.get('alerts/estimate', query));
  }

  /**
   * michollo-client devuelve `error.response.data` cuando la API contesta mal y `undefined`
   * cuando ni contesta (red caída): aquí eso pasa a ser `{ ok: false }` para que quien
   * llama sólo tenga que mirar `ok`.
   */
  private async pedir<T extends RespuestaOk>(llamada: () => Promise<T>): Promise<T> {
    try {
      const resp = await llamada();
      return (resp ?? { ok: false, reason: 'NETWORK' }) as T;
    } catch (error) {
      console.error('[alertas]', error);
      return { ok: false, reason: 'NETWORK' } as T;
    }
  }
}

